/**
 * ─── Admin Page API Service ───
 *
 * All API calls related to the Admin panel live here.
 * Every request uses credentials:"include" so the httpOnly cookie is sent automatically.
 */

import { API_URL } from "../config";

const BASE_URL = `${API_URL}/api/admin`;

/**
 * Fetch all posts (including hidden ones) for the admin.
 * @returns {Promise<Array>}
 */
export async function fetchAdminPosts() {
  const res = await fetch(`${BASE_URL}/posts`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load admin posts");
  return res.json();
}

/**
 * Create a new appointment post.
 * @param {Object} postData  { header, description, date, time, visible }
 * @returns {Promise<Object>} the created post
 */
export async function createPost(postData) {
  const res = await fetch(`${BASE_URL}/posts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(postData),
  });
  if (!res.ok) throw new Error("Failed to create post");
  return res.json();
}

/**
 * Update a post (header, description, date, time).
 * @param {string|number} postId
 * @param {Object}        updates
 * @returns {Promise<Object>} the updated post
 */
export async function updatePost(postId, updates) {
  const res = await fetch(`${BASE_URL}/posts/${postId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update post");
  return res.json();
}

/**
 * Delete a post.
 * @param {string|number} postId
 * @returns {Promise<Object>}
 */
export async function deletePost(postId) {
  const res = await fetch(`${BASE_URL}/posts/${postId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete post");
  return res.json();
}

/**
 * Toggle a post's visibility (show / hide).
 * @param {string|number} postId
 * @param {boolean}       visible
 * @returns {Promise<Object>} the updated post
 */
export async function togglePostVisibility(postId, visible) {
  const res = await fetch(`${BASE_URL}/posts/${postId}/visibility`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ visible }),
  });
  if (!res.ok) throw new Error("Failed to toggle visibility");
  return res.json();
}

/**
 * Load bookings for a specific post.
 * @param {string|number} postId
 * @returns {Promise<Array>} list of booking objects
 */
export async function fetchBookings(postId) {
  const res = await fetch(`${BASE_URL}/posts/${postId}/bookings`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load bookings");
  return res.json();
}

// ─── Members ───

/**
 * Fetch all members for admin.
 * @returns {Promise<Array>}
 */
export async function fetchMembers() {
  const res = await fetch(`${BASE_URL}/members`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load members");
  return res.json();
}

export async function updateMember(memberId, updates) {
  const res = await fetch(`${BASE_URL}/members/${memberId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update member");
  return res.json();
}

// ─── Events ───

/**
 * Fetch all events (including hidden) for admin.
 * @returns {Promise<Array>}
 */
export async function fetchAdminEvents() {
  const res = await fetch(`${BASE_URL}/events`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load events");
  return res.json();
}

/**
 * Create a new event.
 * @param {Object} eventData  { icon, title, description, day, time }
 * @returns {Promise<Object>}
 */
export async function createEvent(eventData) {
  const res = await fetch(`${BASE_URL}/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(eventData),
  });
  if (!res.ok) throw new Error("Failed to create event");
  return res.json();
}

/**
 * Update an event (partial update).
 * @param {string|number} eventId
 * @param {Object}        updates
 * @returns {Promise<Object>}
 */
export async function updateEvent(eventId, updates) {
  const res = await fetch(`${BASE_URL}/events/${eventId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update event");
  return res.json();
}

/**
 * Delete an event.
 * @param {string|number} eventId
 * @returns {Promise<Object>}
 */
export async function deleteEvent(eventId) {
  const res = await fetch(`${BASE_URL}/events/${eventId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete event");
  return res.json();
}

// ─── Seminars ───

export async function fetchAdminSeminarer() {
  const res = await fetch(`${BASE_URL}/seminarer`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load seminars");
  return res.json();
}

export async function createSeminar(data) {
  const res = await fetch(`${BASE_URL}/seminarer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => null); throw new Error(d?.error || "Failed to create seminar"); }
  return res.json();
}

export async function updateSeminar(id, updates) {
  const res = await fetch(`${BASE_URL}/seminarer/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update seminar");
  return res.json();
}

export async function deleteSeminar(id) {
  const res = await fetch(`${BASE_URL}/seminarer/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete seminar");
  return res.json();
}

export async function toggleSeminarVisibility(id, visible) {
  const res = await fetch(`${BASE_URL}/seminarer/${id}/visibility`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ visible }),
  });
  if (!res.ok) throw new Error("Failed to toggle visibility");
  return res.json();
}

export async function fetchSeminarRegistrations(id) {
  const res = await fetch(`${BASE_URL}/seminarer/${id}/registrations`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load registrations");
  return res.json();
}
